import Vue from "vue";
import App from "./App.vue";
import router from "./router";
import store from "./store";
import vuetify from "./plugins/vuetify";
import i18n from "./translations/index.js";
import { createProvider } from "./apolloConfiguration";
import * as inputValidators from "./inputValidators";
import { RETRIEVE_DROPDOWN_DETAILS } from "@/graphql/commonQueries";
import L from "leaflet";
import Config from "@/config";
/* eslint-disable-next-line */
window.$ = window.jQuery = require("jquery");
delete L.Icon.Default.prototype._getIconUrl;

L.Icon.Default.mergeOptions({
  iconRetinaUrl: require("leaflet/dist/images/marker-icon-2x.png"),
  iconUrl: require("leaflet/dist/images/marker-icon.png"),
  shadowUrl: require("leaflet/dist/images/marker-shadow.png"),
});

// configure axios globally
//$http hold axios object so can access axios using $http inside app
import Axios from "axios";
Vue.prototype.$http = Axios;

// Globally Registered Base Components
import "./components/globalComponent.js";

//register perfect scrollbar globally
import VuePerfectScrollbar from "vue-perfect-scrollbar";
Vue.component("VuePerfectScrollbar", VuePerfectScrollbar);

import VueCookies from "vue-cookies";
Vue.use(VueCookies);

Vue.$cookies.config("", "", "", true, "Strict");

import VueApexCharts from "vue-apexcharts";
Vue.use(VueApexCharts);
Vue.component("Apexchart", VueApexCharts);

import { VTooltip, VPopover, VClosePopover } from "v-tooltip";
Vue.directive("tooltip", VTooltip);
Vue.directive("close-popover", VClosePopover);
Vue.component("VPopover", VPopover);

import "./filters/filters";

// import vee-validate components - input validation
import { ValidationObserver, ValidationProvider } from "vee-validate";
Vue.component("ValidationProvider", ValidationProvider);
Vue.component("ValidationObserver", ValidationObserver);

import Clipboard from "v-clipboard";
Vue.use(Clipboard);
// Firebase
import "@/firebase/firebaseConfig";

//analysis performance in devtool
Vue.config.performance = true;
Vue.config.productionTip = false;

// Helper function to set favicon in document head
function changeFavicon(iconPath) {
  // Remove all existing favicon-related link elements
  const existingFavicons = document.querySelectorAll(
    'link[rel="icon"], link[rel="shortcut icon"], link[rel="apple-touch-icon"], link[rel*="icon"]'
  );
  existingFavicons.forEach((link) => {
    link.remove();
  });

  // Create new favicon link elements with different sizes
  const faviconSizes = [
    { rel: "icon", type: "image/x-icon", href: iconPath },
    { rel: "icon", type: "image/png", sizes: "16x16", href: iconPath },
    { rel: "icon", type: "image/png", sizes: "32x32", href: iconPath },
    { rel: "shortcut icon", href: iconPath },
  ];

  // Add new favicon links to the head
  faviconSizes.forEach((favicon) => {
    const link = document.createElement("link");
    link.rel = favicon.rel;
    if (favicon.type) link.type = favicon.type;
    if (favicon.sizes) link.sizes = favicon.sizes;
    link.href = favicon.href;
    document.head.appendChild(link);
  });
}

// Function to update favicon dynamically
async function getGeneralSettings() {
  const {
    clients: { apolloClientAC },
  } = createProvider();

  try {
    const { data } = await apolloClientAC.query({
      query: RETRIEVE_DROPDOWN_DETAILS,
      variables: {
        formId: null,
        key: ["general_settings"],
      },
    });
    const settingsRaw = data?.retrieveDropdownDetails?.dropdownDetails;
    const parsed =
      settingsRaw?.length > 0
        ? JSON.parse(settingsRaw)?.[0]?.data?.[0] ?? {}
        : {};
    const fetchedFavicon = parsed.Favicon_Filename;

    if (fetchedFavicon) localStorage.setItem("faviconFilename", fetchedFavicon);

    return {
      faviconFilename: fetchedFavicon,
    };
  } catch (error) {
    console.error("Failed to fetch general settings", error);
    return null;
  }
}
async function initializeApp() {
  // get general settings
  const generalSettings = await getGeneralSettings();

  // set favicon - try multiple default favicon paths
  const DEFAULT_FAVICON_PATHS = [
    `${location.origin}/favicon.ico`,
    `${location.origin}/in/favicon.ico`,
    `${location.origin}/public/favicon.ico`,
  ];

  if (generalSettings?.faviconFilename) {
    const orgCode = localStorage.getItem("orgCode") ?? store.getters.orgCode;
    const domainName = store.getters.domain;
    const logoPath = `${Config.publicImageS3Path}${domainName}/${orgCode}/favicon/${generalSettings.faviconFilename}`;

    try {
      const response = await fetch(logoPath);
      if (response.ok) {
        changeFavicon(logoPath);
        return;
      }
    } catch (err) {
      console.error("Favicon fetch failed", err);
    }
  }

  // Fallback to default favicon
  // Try to find an existing default favicon that works
  let defaultFaviconFound = false;
  for (const defaultPath of DEFAULT_FAVICON_PATHS) {
    try {
      const response = await fetch(defaultPath);
      if (response.ok) {
        changeFavicon(defaultPath);
        defaultFaviconFound = true;
        break;
      }
    } catch (err) {
      console.warn("Default favicon not found at:", defaultPath);
    }
  }

  // If no default favicon is found, use the first path as fallback
  if (!defaultFaviconFound) {
    changeFavicon(DEFAULT_FAVICON_PATHS[0]);
  }
}

// Call the favicon update function
initializeApp();

new Vue({
  inputValidators,
  router,
  store,
  vuetify,
  i18n,
  apolloProvider: createProvider(),
  render: (h) => h(App),
}).$mount("#app");
